(function () {
  'use strict';

  if (window.Kate3DPreviewRenderer) return;

  const MAX_TEXTURE_CACHE_SIZE = 6;
  const MAX_TEXTURE_WIDTH = 2048;
  const MIN_TEXTURE_WIDTH = 320;
  const MAX_RESOLUTION = 2;
  let pixiLoadPromise = null;

  const ensurePixi = (assetUrl) => {
    if (window.PIXI) return Promise.resolve(window.PIXI);
    if (pixiLoadPromise) return pixiLoadPromise;
    if (!assetUrl) return Promise.reject(new Error('PixiJS asset URL is missing.'));

    pixiLoadPromise = new Promise((resolve, reject) => {
      let script = document.querySelector('script[data-kate-3d-pixi]');
      const handleLoad = () => {
        if (window.PIXI) {
          resolve(window.PIXI);
        } else {
          reject(new Error('PixiJS loaded without exposing PIXI.'));
        }
      };
      const handleError = () => reject(new Error('PixiJS failed to load.'));

      if (!script) {
        script = document.createElement('script');
        script.src = assetUrl;
        script.async = true;
        script.setAttribute('data-kate-3d-pixi', 'true');
      }

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });
      if (!script.isConnected) document.head.appendChild(script);
    }).catch((error) => {
      pixiLoadPromise = null;
      throw error;
    });

    return pixiLoadPromise;
  };

  class Kate3DPreviewRenderer {
    static ensurePixi(assetUrl) {
      return ensurePixi(assetUrl);
    }

    constructor({ container, canvas, onError } = {}) {
      this.container = container;
      this.canvas = canvas;
      this.onError = typeof onError === 'function' ? onError : () => {};
      this.app = null;
      this.wallSprite = null;
      this.floorMesh = null;
      this.resizeObserver = null;
      this.textureCache = new Map();
      this.activeTextureUrls = { wall: '', floor: '' };
      this.sourceTextureUrls = { wall: '', floor: '' };
      this.requestIds = { wall: 0, floor: 0 };
      this.destroyed = false;
      this.contextLostHandler = null;
    }

    async init() {
      if (this.app) return this;
      if (!this.container || !this.canvas) {
        throw new Error('3D preview container or canvas is missing.');
      }
      if (!window.PIXI) {
        throw new Error('PixiJS failed to load.');
      }

      const PIXI = window.PIXI;
      const resolution = Math.min(window.devicePixelRatio || 1, MAX_RESOLUTION);

      PIXI.Assets.setPreferences({ crossOrigin: 'anonymous' });

      this.app = new PIXI.Application();
      await this.app.init({
        canvas: this.canvas,
        width: Math.max(1, this.container.clientWidth),
        height: Math.max(1, this.container.clientHeight),
        backgroundAlpha: 0,
        antialias: true,
        autoDensity: true,
        autoStart: false,
        sharedTicker: false,
        preference: 'webgl',
        preferWebGLVersion: 2,
        powerPreference: 'low-power',
        resolution,
      });
      this.app.stop();

      this.floorMesh = new PIXI.PerspectiveMesh({
        texture: PIXI.Texture.EMPTY,
        verticesX: 20,
        verticesY: 20,
      });
      this.floorMesh.visible = false;

      this.wallSprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
      this.wallSprite.anchor.set(0.5, 1);
      this.wallSprite.visible = false;

      this.app.stage.addChild(this.floorMesh);
      this.app.stage.addChild(this.wallSprite);

      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.container);

      this.contextLostHandler = (event) => {
        event.preventDefault();
        this.onError(new Error('WebGL context was lost.'));
      };
      this.canvas.addEventListener('webglcontextlost', this.contextLostHandler, false);

      this.resize();
      return this;
    }

    isReady() {
      return Boolean(
        this.app &&
        this.wallSprite?.visible &&
        this.floorMesh?.visible &&
        this.activeTextureUrls.wall &&
        this.activeTextureUrls.floor
      );
    }

    async setWallTexture(url) {
      return this.setTexture('wall', url);
    }

    async setFloorTexture(url) {
      return this.setTexture('floor', url);
    }

    async setTexture(type, sourceUrl) {
      if (!['wall', 'floor'].includes(type)) {
        throw new Error(`Unsupported 3D preview texture type: ${type}`);
      }
      if (!this.app || this.destroyed) {
        throw new Error('3D preview renderer is not initialized.');
      }
      if (!sourceUrl) {
        throw new Error(`Missing ${type} preview image.`);
      }

      const requestId = ++this.requestIds[type];
      const textureUrl = this.buildTextureUrl(sourceUrl);
      const texture = await this.loadTexture(textureUrl);

      if (this.destroyed || requestId !== this.requestIds[type]) {
        return { applied: false, stale: true };
      }

      this.sourceTextureUrls[type] = sourceUrl;
      this.activeTextureUrls[type] = textureUrl;

      if (type === 'wall') {
        this.wallSprite.texture = texture;
        this.wallSprite.visible = true;
      } else {
        this.floorMesh.texture = texture;
        this.floorMesh.visible = true;
      }

      this.markTextureUsed(textureUrl);
      this.resize();
      this.pruneTextureCache();

      return { applied: true, stale: false, url: textureUrl };
    }

    buildTextureUrl(sourceUrl) {
      try {
        const url = new URL(sourceUrl, document.baseURI);
        if (!url.hostname.endsWith('cdn.shopify.com')) return url.href;

        const resolution = Math.min(window.devicePixelRatio || 1, MAX_RESOLUTION);
        const requestedWidth = Math.min(
          MAX_TEXTURE_WIDTH,
          Math.max(MIN_TEXTURE_WIDTH, Math.ceil((this.container.clientWidth * resolution) / 100) * 100)
        );
        url.searchParams.set('width', String(requestedWidth));
        return url.href;
      } catch (error) {
        return sourceUrl;
      }
    }

    async loadTexture(url) {
      const cached = this.textureCache.get(url);
      if (cached) {
        cached.lastUsed = Date.now();
        return cached.promise;
      }

      const entry = {
        lastUsed: Date.now(),
        promise: window.PIXI.Assets.load(url),
      };
      this.textureCache.set(url, entry);

      try {
        return await entry.promise;
      } catch (error) {
        this.textureCache.delete(url);
        throw error;
      }
    }

    markTextureUsed(url) {
      const entry = this.textureCache.get(url);
      if (entry) entry.lastUsed = Date.now();
    }

    pruneTextureCache() {
      if (this.textureCache.size <= MAX_TEXTURE_CACHE_SIZE) return;

      const activeUrls = new Set(Object.values(this.activeTextureUrls));
      const removable = [...this.textureCache.entries()]
        .filter(([url]) => !activeUrls.has(url))
        .sort((a, b) => a[1].lastUsed - b[1].lastUsed);

      while (this.textureCache.size > MAX_TEXTURE_CACHE_SIZE && removable.length) {
        const [url] = removable.shift();
        this.textureCache.delete(url);
        window.PIXI.Assets.unload(url).catch(() => {});
      }
    }

    resize() {
      if (!this.app || this.destroyed) return;

      const width = Math.max(1, Math.round(this.container.clientWidth));
      const height = Math.max(1, Math.round(this.container.clientHeight));
      this.app.renderer.resize(width, height);

      const horizonY = height * 0.59;
      let wallWidth = width * 0.58;

      if (this.wallSprite?.visible && this.wallSprite.texture) {
        const textureWidth = Math.max(1, this.wallSprite.texture.width || 1);
        const textureHeight = Math.max(1, this.wallSprite.texture.height || 1);
        const scale = Math.min(wallWidth / textureWidth, (height * 0.54) / textureHeight);
        this.wallSprite.width = textureWidth * scale;
        this.wallSprite.height = textureHeight * scale;
        this.wallSprite.position.set(width * 0.5, horizonY);
        wallWidth = this.wallSprite.width;
      }

      if (this.floorMesh) {
        const topLeftX = (width - wallWidth) * 0.5;
        const topRightX = topLeftX + wallWidth;
        this.floorMesh.setCorners(
          topLeftX,
          horizonY,
          topRightX,
          horizonY,
          width * 0.96,
          height * 0.92,
          width * 0.04,
          height * 0.92
        );
      }

      this.render();
    }

    render() {
      if (!this.app || this.destroyed) return;
      this.app.renderer.render(this.app.stage);
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.requestIds.wall += 1;
      this.requestIds.floor += 1;
      this.resizeObserver?.disconnect();

      if (this.contextLostHandler) {
        this.canvas?.removeEventListener('webglcontextlost', this.contextLostHandler, false);
      }

      if (this.wallSprite) {
        this.wallSprite.texture = window.PIXI?.Texture?.EMPTY;
      }
      if (this.floorMesh) {
        this.floorMesh.texture = window.PIXI?.Texture?.EMPTY;
      }

      const textureUrls = [...this.textureCache.keys()];
      this.textureCache.clear();
      this.activeTextureUrls = { wall: '', floor: '' };

      this.app?.destroy();
      this.app = null;

      if (window.PIXI && textureUrls.length) {
        Promise.allSettled(textureUrls.map((url) => window.PIXI.Assets.unload(url))).catch(() => {});
      }
    }
  }

  window.Kate3DPreviewRenderer = Kate3DPreviewRenderer;
})();
